// Idle / welcome screen — port of mockup KioskWelcome (pdks/kiosk.jsx):
// date·time eyebrow, big serif greeting with italic em half, lead line,
// a primary presence QR and a PIN fallback. The mockup's visitor CTA has
// NO backend and is intentionally not ported (see batch notes).

import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { KioskPresenceCodeFetcher } from "@/api/punch";
import { useNow } from "@/lib/useNow";
import { useOnline } from "@/lib/useOnline";
import { QrScreen, type QrHealth } from "@/screens/QrScreen";

type IdleStatus = "demo" | "offline" | "connecting" | "unreachable" | "ready";

const STATUS_DOT: Record<IdleStatus, string> = {
  demo: "bg-[var(--color-warning-500)]",
  offline: "bg-[var(--state-danger)]",
  connecting: "bg-[var(--border-default)]",
  unreachable: "bg-[var(--state-danger)]",
  ready: "bg-[var(--color-accent-500)]",
};

function idleStatus(demoMode: boolean, online: boolean, qr: QrHealth): IdleStatus {
  if (demoMode) return "demo";
  if (!online) return "offline";
  if (qr === "pending") return "connecting";
  return qr === "ok" ? "ready" : "unreachable";
}

export interface IdleScreenProps {
  readonly demoMode: boolean;
  readonly onStart: () => void;
  readonly fetchPresenceCode?: KioskPresenceCodeFetcher | undefined;
}

export function IdleScreen({ demoMode, onStart, fetchPresenceCode }: IdleScreenProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const now = useNow();
  const online = useOnline();
  const [qrHealth, setQrHealth] = useState<QrHealth>("pending");
  const status = idleStatus(demoMode, online, qrHealth);

  const date = new Intl.DateTimeFormat(i18n.language, {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(now);
  const time = new Intl.DateTimeFormat(i18n.language, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(now);

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center overflow-y-auto px-6 pb-6 pt-3 sm:px-16">
      <p className="mb-3 font-sans text-[14px] font-semibold uppercase tracking-[0.1em] text-[var(--accent)]">
        {t("kiosk.idle.datetime", { date, time })}
      </p>
      <h1 className="m-0 text-center font-serif text-[clamp(30px,4.5vw,52px)] font-normal leading-[1.04] tracking-[-0.02em] text-[var(--fg-primary)]">
        {t("kiosk.idle.greeting")} <em className="italic">{t("kiosk.idle.greeting_em")}</em>
      </h1>
      <p className="mt-3 max-w-[540px] text-center font-serif text-[18px] text-[var(--fg-secondary)]">
        {t("kiosk.idle.lead")}
      </p>

      <QrScreen fetchPresenceCode={fetchPresenceCode} onHealthChange={setQrHealth} />

      <button
        type="button"
        onClick={onStart}
        className="mt-5 flex max-w-full shrink-0 cursor-pointer flex-col items-center gap-1 rounded-2xl border border-[var(--border-default)] bg-[var(--bg-raised)] px-6 py-3 text-[var(--fg-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)]"
      >
        <span className="font-sans text-[17px] font-semibold">
          {t("kiosk.idle.punch_cta")}
        </span>
        <span className="font-serif text-[15px] text-[var(--fg-secondary)]">
          {t("kiosk.idle.punch_cta_sub")}
        </span>
      </button>

      {/* Status pill — mockup's "Okuyucu bağlı" bar. "Ready" only when the
          device is online AND the server just issued a presence code; a
          transport that merely exists says nothing about either. */}
      <div
        aria-live="polite"
        data-status={status}
        className="mt-4 flex shrink-0 items-center gap-2.5 rounded-full border border-[var(--border-subtle)] bg-[var(--bg-raised)] px-5 py-2"
      >
        <span aria-hidden="true" className={`h-[9px] w-[9px] shrink-0 rounded-full ${STATUS_DOT[status]}`} />
        <span className="font-sans text-[14px] font-medium text-[var(--fg-muted)]">
          {t(`kiosk.status.${status}`)}
        </span>
      </div>
    </div>
  );
}
